import React from 'react';
import { Calendar, CheckCircle, XCircle, RotateCcw } from 'lucide-react';
import { Visita, VisitaStato } from '../../types';

// Il badge del tipo (icona/colore) viene dall'anagrafica activity_types via
// useActivityTypes()/resolveActivityType() — vedi hooks/useActivityTypes.ts.
// Qui resta solo lo STATO_CONFIG, che non fa parte dell'anagrafica.

/**
 * Stato styling. `color` is the pill (badge) look used by both views; `dot` and
 * `bar` are the calendar-only accents (a status dot and a left border on the
 * per-day chips). Class strings are written out in full so Tailwind's JIT keeps
 * them.
 */
export const STATO_CONFIG: Record<
  VisitaStato,
  { label: string; icon: React.ReactNode; color: string; dot: string; bar: string }
> = {
  programmata: {
    label: 'Programmata',
    icon: <Calendar className="h-3.5 w-3.5" />,
    color: 'bg-yellow-100 text-yellow-700',
    dot: 'bg-yellow-400',
    bar: 'border-l-yellow-400',
  },
  completata: {
    label: 'Completata',
    icon: <CheckCircle className="h-3.5 w-3.5" />,
    color: 'bg-green-100 text-green-700',
    dot: 'bg-green-500',
    bar: 'border-l-green-500',
  },
  annullata: {
    label: 'Annullata',
    icon: <XCircle className="h-3.5 w-3.5" />,
    color: 'bg-red-100 text-red-700',
    dot: 'bg-red-500',
    bar: 'border-l-red-500',
  },
  rinviata: {
    label: 'Rinviata',
    icon: <RotateCcw className="h-3.5 w-3.5" />,
    color: 'bg-orange-100 text-orange-700',
    dot: 'bg-orange-400',
    bar: 'border-l-orange-400',
  },
};

export function statoConfig(stato: VisitaStato) {
  return STATO_CONFIG[stato] || STATO_CONFIG.programmata;
}

/** Human label for a visit's customer, tolerant of missing pieces. */
export function clienteName(visita: Visita): string {
  if (visita.categoria_interna) return `Attività interna · ${INTERNAL_CATEGORIES[visita.categoria_interna]}`;
  const c = visita.cliente;
  if (!c) return '—';
  if (c.ragione_sociale) return c.ragione_sociale;
  return `${c.nome} ${c.cognome || ''}`.trim() || '—';
}

/** Human label for a visit's seller (venditore), tolerant of missing pieces. */
export function venditoreName(visita: Visita): string {
  const u = visita.user;
  if (!u) return '—';
  const full = `${u.nome || ''} ${u.cognome || ''}`.trim();
  return full || u.name || u.email || '—';
}

/** `HH:MM` from a backend `HH:MM:SS` time, or empty when the visit has no time. */
export function formatOra(ora?: string | null): string {
  return ora ? ora.substring(0, 5) : '';
}

export const INTERNAL_CATEGORIES = { ferie: 'Ferie', riunione: 'Riunione', permesso: 'Permesso', malattia: 'Malattia', servizio_banco: 'Servizio al banco', magazzino: 'Giornata in magazzino' } as const;
